import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconUserPlus,
  IconShieldLock,
  IconUsers,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNotifications } from '../../hooks/useNotifications';
import { useTenantUsers, userDisplayName } from '../../hooks/useTenantUsers';
import { useCurrentUserPermissions } from '../../hooks/useCurrentUserPermissions';
import { TenantUserDto } from '../../api/model';
import {
  useGetRoleManagementRoles,
  usePostRoleManagementUsersUserIdRole,
  useDeleteRoleManagementUsersUserIdRole,
  useDeleteRoleManagementUsersUserId,
  getGetRoleManagementUsersQueryKey,
  getGetRoleManagementUsersMePermissionsQueryKey,
} from '../../api/role-management-api/role-management-api';
import { useGetAuthUser } from '../../api/auth-api/auth-api';
import ConfirmationDialog from '../ui/ConfirmationDialog/ConfirmationDialog';
import AddUserModal from './AddUserModal';
import RolePermissionsMatrix from './RolePermissionsMatrix';
import styles from './ManageUsers.module.scss';

type ManageUsersTab = 'directory' | 'permissions';

// First two letter of name avatar chippy badgess
function initials(user: TenantUserDto): string {
  const combined = `${(user.firstName?.[0] || '').toUpperCase()}${(user.lastName?.[0] || '').toUpperCase()}`.trim();
  return combined || (user.username?.[0] || '?').toUpperCase();
}

interface PendingAssign {
  userId: number;
  userName: string;
  fromName?: string;
  toRoleId: number | null;
  toName: string;
}

const LICENSE_USER_LIMITS: Record<string, number> = {
  PILOT: 5,
  BASIC: 20,
  PRO: 100,
  ENTERPRISE: -1,
};

const ManageUsers: React.FC = () => {
  const { t } = useTranslation('settings');
  const { showSuccess, showError } = useNotifications();
  const queryClient = useQueryClient();
  const { isSystemAdmin, roleKey } = useCurrentUserPermissions();
  const { data: currentUser } = useGetAuthUser();

  const [activeTab, setActiveTab] = useState<ManageUsersTab>('directory');
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [pending, setPending] = useState<PendingAssign | null>(null);
  const [pendingDelete, setPendingDelete] = useState<TenantUserDto | null>(null);

  const { users, isLoading: isLoadingUsers } = useTenantUsers();
  const { data: tenantRoles, isLoading: isLoadingRoles } = useGetRoleManagementRoles();
  const assignRoleMutation = usePostRoleManagementUsersUserIdRole();
  const unassignRoleMutation = useDeleteRoleManagementUsersUserIdRole();
  const deleteUserMutation = useDeleteRoleManagementUsersUserId();

  // matches backend
  const canManage = isSystemAdmin || roleKey === 'ADMIN';

  const maxUsers = !isSystemAdmin && currentUser?.licenseType ? (LICENSE_USER_LIMITS[currentUser.licenseType] ?? -1) : -1;
  const atUserLimit = maxUsers !== -1 && users.length >= maxUsers;

  const roleNameById = useMemo(() => {
    const m = new Map<number, string>();
    (tenantRoles ?? []).forEach(r => {
      if (r.id != null) m.set(r.id, r.displayName ?? '');
    });
    return m;
  }, [tenantRoles]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...users].sort((a, b) =>
      userDisplayName(a).localeCompare(userDisplayName(b))
    );
    if (!q) return sorted;
    return sorted.filter(u => {
      const hay = `${u.firstName ?? ''} ${u.lastName ?? ''} ${u.username ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [users, search]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: getGetRoleManagementUsersQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetRoleManagementUsersMePermissionsQueryKey() });
  };

  // staged when the user changes a row's role select
  const stageAssign = (user: TenantUserDto, roleIdRaw: string) => {
    if (!user.id) return;
    if (roleIdRaw === '') {
      if (user.tenantRoleId == null) return;
      setPending({
        userId: user.id,
        userName: userDisplayName(user),
        fromName: user.tenantRoleDisplayName ?? undefined,
        toRoleId: null,
        toName: '',
      });
      return;
    }
    const toRoleId = parseInt(roleIdRaw, 10);
    if (!toRoleId || toRoleId === user.tenantRoleId) return;
    setPending({
      userId: user.id,
      userName: userDisplayName(user),
      fromName: user.tenantRoleDisplayName ?? undefined,
      toRoleId,
      toName: roleNameById.get(toRoleId) ?? '',
    });
  };

  const confirmAssign = () => {
    if (!pending) return;
    const target = pending;
    setPending(null);

    if (target.toRoleId === null) {
      unassignRoleMutation.mutate(
        { userId: target.userId },
        {
          onSuccess: () => {
            showSuccess(
              t('user.manageUsers.feedback.roleRemoved', 'Role removed'),
              t('user.manageUsers.feedback.roleRemovedMsg', { user: target.userName, defaultValue: '{{user}} has no role assigned.' })
            );
            refresh();
          },
          onError: () => showError(
            t('user.manageUsers.feedback.roleAssignFailed'),
            t('user.manageUsers.feedback.roleAssignFailedMsg')
          ),
        }
      );
      return;
    }

    assignRoleMutation.mutate(
      { userId: target.userId, params: { roleId: target.toRoleId } },
      {
        onSuccess: () => {
          showSuccess(
            t('user.manageUsers.feedback.roleAssigned'),
            t('user.manageUsers.feedback.roleAssignedNamed', {
              user: target.userName,
              role: target.toName,
              defaultValue: '{{role}} assigned to {{user}}.',
            })
          );
          refresh();
        },
        onError: () => {
          showError(
            t('user.manageUsers.feedback.roleAssignFailed'),
            t('user.manageUsers.feedback.roleAssignFailedMsg')
          );
        },
      }
    );
  };

  const confirmDelete = () => {
    const userId = pendingDelete?.id;
    if (!userId) return;
    const target = pendingDelete!;
    deleteUserMutation.mutate(
      { userId },
      {
        onSuccess: () => {
          showSuccess(
            t('user.manageUsers.feedback.userDeleted', 'User deleted'),
            t('user.manageUsers.feedback.userDeletedMsg', {
              user: userDisplayName(target),
              defaultValue: '{{user}} has been removed.',
            })
          );
          refresh();
        },
        onError: () => {
          showError(
            t('user.manageUsers.feedback.deleteFailed', 'Delete failed'),
            t('user.manageUsers.feedback.deleteFailedMsg', 'Could not delete the user.')
          );
        },
      }
    );
    setPendingDelete(null);
  };

  return (
    <div className={styles.manageUsers}>
      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'directory' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('directory')}
        >
          <IconUsers size={18} />
          <span>{t('user.manageUsers.tabDirectory', 'Users')}</span>
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'permissions' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('permissions')}
        >
          <IconShieldLock size={18} />
          <span>{t('user.manageUsers.tabPermissions')}</span>
        </button>
      </div>

      {activeTab === 'permissions' ? (
        <RolePermissionsMatrix editable={isSystemAdmin} />
      ) : (
        <div className={styles.directory}>
          <div className={styles.directoryHead}>
            <div>
              <h2 className={styles.title}>{t('user.manageUsers.directory.title', 'Users')}</h2>
              <p className={styles.description}>
                {t('user.manageUsers.directory.subtitle', 'Everyone in your tenant. Assign a role to control what each person can access.')}
              </p>
            </div>
            {canManage && (
              <div className={styles.addUserWrapper}>
                {atUserLimit && (
                  <span className={styles.limitBadge}>
                    {users.length}/{maxUsers} users — limit reached
                  </span>
                )}
                <button
                  type="button"
                  className={styles.addUserButton}
                  onClick={() => setAddOpen(true)}
                  disabled={atUserLimit}
                  title={atUserLimit ? `Your ${currentUser?.licenseType} plan allows up to ${maxUsers} users.` : undefined}
                >
                  <IconUserPlus size={18} />
                  {t('user.manageUsers.directory.addUser', 'Add user')}
                </button>
              </div>
            )}
          </div>

          <div className={styles.searchBar}>
            <IconSearch size={18} className={styles.searchIcon} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('user.manageUsers.directory.search', 'Search by name or username…')}
            />
          </div>

          <div className={styles.userTable}>
            <div className={styles.tableHeadRow}>
              <span>{t('user.manageUsers.directory.colName', 'Name')}</span>
              <span>{t('user.manageUsers.directory.colMatrixRole', 'Role')}</span>
              <span />
            </div>

            {isLoadingUsers ? (
              <div className={styles.tableEmpty}>{t('user.manageUsers.directory.loading', 'Loading users…')}</div>
            ) : filteredUsers.length === 0 ? (
              <div className={styles.tableEmpty}>
                {search.trim()
                  ? t('user.manageUsers.directory.noResults', 'No users match your search.')
                  : t('user.manageUsers.directory.empty', 'No users yet.')}
              </div>
            ) : (
              filteredUsers.map(user => {
                const isSysAdmin = user.systemRole === 'SUPER_ADMIN';
                return (
                <div key={user.id ?? user.username} className={styles.userRow}>
                  <div className={styles.userCell}>
                    <span className={styles.avatar}>{initials(user)}</span>
                    <div className={styles.userMain}>
                      <div className={styles.userNameRow}>
                        <span className={styles.userName}>{userDisplayName(user)}</span>
                        {isSysAdmin && (
                          <span className={styles.sysAdminBadge} title={t('user.manageUsers.directory.sysAdminTitle', 'System administrator')}>
                            <IconShieldLock size={11} />
                            {t('user.manageUsers.directory.sysAdmin', 'System Admin')}
                          </span>
                        )}
                      </div>
                      <span className={styles.userHandle}>@{user.username}</span>
                    </div>
                  </div>

                  <div className={styles.matrixRoleCell}>
                    {canManage && !isSysAdmin ? (
                      <select
                        className={`${styles.roleSelect} ${user.tenantRoleId == null ? styles.roleSelectEmpty : ''}`}
                        value={user.tenantRoleId != null ? String(user.tenantRoleId) : ''}
                        onChange={e => stageAssign(user, e.target.value)}
                        disabled={isLoadingRoles || assignRoleMutation.isPending || unassignRoleMutation.isPending}
                      >
                        <option value="">
                          {user.tenantRoleId != null
                            ? t('user.manageUsers.directory.unassignRole', '— Remove role —')
                            : t('user.manageUsers.directory.noRole', 'No role — set one')}
                        </option>
                        {(tenantRoles ?? []).map(role => (
                          <option key={role.id} value={role.id}>
                            {role.displayName}
                          </option>
                        ))}
                      </select>
                    ) : user.tenantRoleDisplayName ? (
                      <span className={styles.roleBadge}>{user.tenantRoleDisplayName}</span>
                    ) : (
                      <span className={styles.roleBadgeNone}>{t('user.manageUsers.directory.noRoleShort', 'No role')}</span>
                    )}
                  </div>

                  <div className={styles.actionCell}>
                    {canManage && user.id !== currentUser?.id && !isSysAdmin && (
                      <button
                        type="button"
                        className={styles.deleteUserButton}
                        onClick={() => setPendingDelete(user)}
                        title={t('user.manageUsers.directory.deleteUser', 'Remove user')}
                      >
                        <IconTrash size={15} />
                      </button>
                    )}
                  </div>
                </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <AddUserModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={refresh}
        tenantRoles={tenantRoles ?? []}
        isLoadingRoles={isLoadingRoles}
      />

      <ConfirmationDialog
        isOpen={!!pending}
        onClose={() => setPending(null)}
        onConfirm={confirmAssign}
        type="confirm"
        title={t('user.manageUsers.confirm.assignTitle', 'Change role?')}
        message={
          pending?.toRoleId === null
            ? t('user.manageUsers.confirm.unassignMessage', {
                user: pending?.userName ?? '',
                defaultValue: 'Remove the current role from {{user}}? They will lose all access until a new role is assigned.',
              })
            : pending?.fromName
              ? t('user.manageUsers.confirm.assignMessage', {
                  user: pending?.userName ?? '',
                  from: pending?.fromName ?? '',
                  to: pending?.toName ?? '',
                  defaultValue: 'Change {{user}} from {{from}} to {{to}}?',
                })
              : t('user.manageUsers.confirm.assignMessageNew', {
                  user: pending?.userName ?? '',
                  to: pending?.toName ?? '',
                  defaultValue: 'Assign the role {{to}} to {{user}}?',
                })
        }
        confirmText={pending?.toRoleId === null
          ? t('user.manageUsers.confirm.unassignConfirm', 'Remove role')
          : t('user.manageUsers.confirm.assignConfirm', 'Assign role')}
        cancelText={t('user.manageUsers.cancel', 'Cancel')}
      />

      <ConfirmationDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        type="danger"
        title={t('user.manageUsers.confirm.deleteTitle', 'Remove user?')}
        message={t('user.manageUsers.confirm.deleteMessage', {
          user: pendingDelete ? userDisplayName(pendingDelete) : '',
          defaultValue: 'Permanently remove {{user}} from this tenant? This cannot be undone.',
        })}
        confirmText={t('user.manageUsers.confirm.deleteConfirm', 'Remove')}
        cancelText={t('user.manageUsers.cancel', 'Cancel')}
      />
    </div>
  );
};

export default ManageUsers;
